import React from "react";
import { useRef } from "react";
import { PriceRanges } from "../../data";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { Spacer } from "../../offering/spacer";
import { useMeetingRoomDailyPricingFeatureFlag } from "../../core/splitio";
import { SpaceBulkDiscount } from "../utils/get_space_bulk_discount";
import styles from "./meeting_room_gallery_card_details.module.css";
import classNames from "classnames";
import { Icon } from "../../components";
import { Template } from "@/data/partner_offering";
import { useFromPriceLabel } from "../hooks/use_from_price_label";

interface GalleryRoomCardDetailsProps {
  hourlyPrice?: number;
  dailyPrice?: number;
  currency: string | null;
  capacity: number;
  partnerOfferingID: string;
  priceRanges?: PriceRanges;
  bulkDiscount?: SpaceBulkDiscount;
  partnerID: string;
  template?: Template;
}

export function GalleryRoomCardDetails(props: GalleryRoomCardDetailsProps) {
  const {
    hourlyPrice,
    dailyPrice,
    currency,
    capacity,
    priceRanges,
    bulkDiscount,
    partnerID,
    template,
  } = props;
  const { fbt, locale } = useI18n();
  const priceWrapperRef = useRef<HTMLDivElement>(null);

  const meetingRoomDailPricingFeatureFlag =
    useMeetingRoomDailyPricingFeatureFlag(partnerID);

  const { hourlyPriceLabel, dailyPriceLabel } = useFromPriceLabel(
    priceRanges,
    hourlyPrice,
    currency,
    locale.languageTag,
    fbt
  );

  const bulkDiscountComp = bulkDiscount?.saving ? (
    <div
      data-testid="bulk-discount-text"
      className={classNames(
        styles.offeringBulkDiscountText,
        "offering-bulk-discount-text"
      )}
    >
      {fbt(
        "Book " +
          fbt.param("numberOfHours", bulkDiscount.minBookingUnit) +
          "+ hours, save " +
          fbt.param("savingDisplay", bulkDiscount.saving)
      )}
    </div>
  ) : null;

  const getCapacityText = () => {
    if (capacity === 1) {
      return `1 ${fbt("person")}`;
    }

    const maxCapacityText = fbt(
      "Up to " + fbt.param("capacity", capacity) + " people"
    );
    if (template === Template.EventSpace) {
      return maxCapacityText + `, ${fbt("varies by layout")}`;
    } else {
      return maxCapacityText;
    }
  };

  return (
    <div className={classNames("card-details", styles.meetingRoomCardDetail)}>
      <div className={classNames(styles.priceWrapper)} ref={priceWrapperRef}>
        {dailyPriceLabel && meetingRoomDailPricingFeatureFlag ? (
          <div>
            <div
              data-testid="price-with-daily"
              className={styles.withDailyPrice}
            >
              {hourlyPriceLabel ? (
                <>
                  {hourlyPriceLabel}
                  <div>&#x2022;</div>
                  <div data-testid="save-tag" className={styles.saveTag}>
                    <Icon
                      className="save-tag-icon"
                      color="inherit"
                      name="price-tag"
                      size={16}
                    />
                  </div>
                </>
              ) : undefined}

              {dailyPriceLabel}
            </div>
            <div>{bulkDiscountComp}</div>
          </div>
        ) : (
          <div data-testid="price-only-hourly">
            {hourlyPriceLabel}
            {bulkDiscount &&
              (!dailyPrice || !meetingRoomDailPricingFeatureFlag) && (
                <>{bulkDiscountComp}</>
              )}
          </div>
        )}
      </div>
      <Spacer size={8} />
      <div>
        <Icon color="white" className="people-icon" name="people" />{" "}
        {getCapacityText()}
      </div>
    </div>
  );
}
